/* ==========================================================================
   CADOO PERSONALIZED GIFTS - product form behaviour
   --------------------------------------------------------------------------
   Works with snippets/cadoo-personalization.liquid (the fields) and the PDP
   add path in assets/cadoo.js (setupPdp -> doAdd). cadoo.js asks
   window.CadooPersonalize.forRoot(root) for a controller; a product with no
   personalization fields returns null and cadoo.js adds it exactly as before.

   METHODS. A product allows one, two or three methods:
     name_initials  "Name or initials"        Name or initials, Font, Print colour
     letter_name    "Monogram + name"         Letter, Letter colour, Name,
                                              Name colour, Name font
     photo_upload   "Upload a photo or logo"  Your photo
   plus the shared optional "Personalization notes".
   Exactly ONE method is submitted per cart line:
     - every method's fields sit in their own <fieldset data-cd-pz-panel>;
       the inactive ones are hidden AND disabled, so the browser leaves them
       out of the form data and validation;
     - pruneInactive() then applies a per-method whitelist to the request;
     - values typed into an inactive method stay on the page as a draft for
       convenience, but are never sent.
   Switching method is locked while a photo is uploading (the file travels
   inside the add request itself, so a finished upload can never attach to a
   text line).

   UPLOAD (unchanged architecture). The ORIGINAL File is posted as multipart
   FormData to /cart/add.js (or /cart/change.js by line key when editing);
   Shopify stores it and returns a hosted cdn.shopify.com/.../uploads/ URL
   in properties["Your photo"]. Panel states: empty -> ready -> uploading ->
   done | error (kept for retry). Only "done" is described as saved. A line
   accepted without a hosted URL is removed again and reported as a failure.

   SEPARATE LINES. Every attempt carries a private _cadoo_op id, so different
   personalizations of the same variant stay separate lines, and after a
   network failure the cart is re-read - a line bearing that id means the add
   DID happen, so a retry never duplicates.

   EDIT CONTEXT (?cd_edit=<line key>). Prefills the method and its values
   from the bag line, keeps the saved photo unless a new one is chosen, and
   saves with ONE request against that key. Only NON-personalization
   properties of the old line are carried over (gift grouping, wrapping,
   add-on keys); the personalization is rebuilt from the active method, so
   cleared notes or another method's fields can never come back. A saved
   value that is no longer offered (e.g. a "(draft)" font) is shown as
   "Previously chosen" and kept unless the customer picks a new one - it is
   never silently remapped.

   TEXT. No length caps, counters, character patterns, case changes or
   transliteration. Required fields reject blank/whitespace-only input; text
   is trimmed at the ends only.
   ========================================================================== */
(function () {
  'use strict';

  var ALLOWED_TYPES = ['image/jpeg', 'image/png'];
  var ALLOWED_EXT = /\.(jpe?g|png)$/i;
  var HOSTED = /^https:\/\/cdn\.shopify\.com\/.+\/uploads\//i;
  var LABELS = { name_initials: 'Name or initials', letter_name: 'Monogram + name', photo_upload: 'Upload a photo or logo' };
  /* Line-property keys each method may send (besides the shared ones). */
  var KEYS = {
    name_initials: ['Name or initials', 'Font', 'Print colour'],
    letter_name: ['Letter', 'Letter colour', 'Name', 'Name colour', 'Name font'],
    photo_upload: ['Your photo']
  };
  var SHARED = ['_cadoo_personalized', 'Personalization', 'Personalization notes'];
  /* Every personalization key, current and legacy. Never carried over from an
     old bag line - the active method rebuilds them. */
  var PZ_ALL = ['_cadoo_personalized', '_cadoo_op', 'Personalization', 'Personalization notes', 'Instructions',
    'Name or initials', 'Font', 'Print colour', 'Letter', 'Letter colour', 'Name', 'Name colour', 'Name font',
    'Initials', 'Method', 'Your photo'];
  var T = {
    missingPhoto: 'Please choose your photo or logo to continue.',
    badType: 'That file type isn’t supported. Please choose a JPG or PNG. HEIC, PDF or SVG? Save it as a JPG or PNG first.',
    tooBig: 'This photo exceeds the {mb} MB limit. Please choose a smaller file.',
    unreadable: 'We couldn’t open this file as a photo. Please choose a different JPG or PNG.',
    checking: 'One moment - we’re still checking your photo.',
    busy: 'Your photo is still uploading. Please wait for it to finish.',
    addFail: 'We couldn’t add your item. Please try again.',
    saveFail: 'We couldn’t save your changes. Please try again.',
    tooLong: 'We couldn’t save this personalization as typed. Your text is still here - please contact us and we’ll take your order by hand.',
    noLine: 'That item is no longer in your bag. Add it again below.',
    another: 'Your last photo is already in your bag. To add another, choose a new photo (or change the quantity in your bag).',
    blocked: 'Personalization for this item isn’t available online right now, so it can’t be added to your bag. Please contact us and we’ll help you order it.',
    methodGone: 'The personalization saved on this item isn’t offered any more. Please choose one of the options below.',
    adding: 'Uploading…',
    addingPlain: 'Adding…',
    saving: 'Saving…',
    save: 'Save changes',
    stReady: 'Photo selected. Tap Add to cart to upload and save it.',
    stReadyEdit: 'Photo selected. Tap Save changes to upload and save it.',
    stUploading: 'Uploading your photo… {pct}%',
    stUploadingNoPct: 'Uploading your photo…',
    stDone: 'Added to your bag with your photo.',
    stError: 'Your photo couldn’t be uploaded. It’s still selected. Tap Add to cart to try again.',
    stErrorEdit: 'Your photo couldn’t be uploaded. It’s still selected. Tap Save changes to try again.',
    stKept: 'Saved photo. It stays unless you replace it.',
    stChecking: 'Checking photo…',
    insAdd: 'Add personalization notes (optional)',
    insEdit: 'Personalization notes added · Edit',
    insHide: 'Hide personalization notes',
    previously: 'Previously chosen: {v}'
  };
  /* One byte threshold for validation AND every displayed size: decimal
     megabytes (1 MB = 1,000,000 bytes). Shopify's native cart upload (tested
     2026-10-07) accepted 20,000,000-byte JPG/PNG files and rejected 20 MiB
     (20,971,520 bytes) and above with HTTP 413. */
  var MB = 1000000;

  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function fmt(s, o) { return String(s).replace(/\{(\w+)\}/g, function (m, k) { return o && o[k] !== undefined ? o[k] : m; }); }
  function mb(bytes) { return (Math.ceil(bytes / MB * 10) / 10).toFixed(1).replace(/\.0$/, ''); }
  function sizeLabel(bytes) { return bytes < MB ? Math.max(1, Math.round(bytes / 1000)) + ' KB' : mb(bytes) + ' MB'; }
  function opId() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').slice(0, 20); } catch (e) {}
    return (Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
  }
  function readJson(el) { if (!el) return null; try { return JSON.parse(el.textContent); } catch (e) { return null; } }
  function routes() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return { add: r + 'cart/add.js', change: r + 'cart/change.js', cart: r + 'cart.js', cartPage: r.replace(/\/$/, '') + '/cart' };
  }
  function getCart() {
    return fetch(routes().cart, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { return (c && Array.isArray(c.items)) ? c : null; })
      .catch(function () { return null; });
  }
  function hostedUrl(v) { return typeof v === 'string' && HOSTED.test(v); }
  function safeHref(v) { return typeof v === 'string' && /^https:\/\//i.test(v) ? v : ''; }
  function sized(url, w) { return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }
  function blank(v) { return v == null || String(v).trim() === ''; }

  /* Which method a saved bag line used (current and legacy values). */
  function lineMethod(p) {
    var v = (p && p.Personalization) || '';
    /* "Letter + name" = the earlier label of the same method (lines added before the rename). */
    if (v === LABELS.letter_name || v === 'Letter + name') return 'letter_name';
    if (v === LABELS.photo_upload || v === 'Photo') return 'photo_upload';
    if (v === LABELS.name_initials || v === 'Name' || v === 'Initials') return 'name_initials';
    if (p && hostedUrl(p['Your photo'])) return 'photo_upload';
    if (p && (p['Name or initials'] || p.Name || p.Initials)) return 'name_initials';
    return '';
  }

  /* multipart POST with upload progress; resolves {status, ok, text}, rejects on network failure */
  function postForm(url, fd, onProgress, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var x = new XMLHttpRequest();
      x.open('POST', url, true);
      x.setRequestHeader('Accept', 'application/json');
      x.timeout = timeoutMs || 120000;
      if (x.upload && onProgress) {
        x.upload.onprogress = function (e) { if (e.lengthComputable && e.total) onProgress(e.loaded / e.total); };
      }
      x.onload = function () { resolve({ status: x.status, ok: x.status >= 200 && x.status < 300, text: x.responseText }); };
      x.onerror = function () { reject(new Error('network')); };
      x.ontimeout = function () { reject(new Error('timeout')); };
      x.onabort = function () { reject(new Error('abort')); };
      x.send(fd);
    });
  }

  /* Secondary links follow the selected variant. */
  function syncLinks(root, id) {
    var own = $('[data-cd-pz-own]', root);
    if (own) {
      var map = readJson($('[data-cd-pz-own-map]', own)) || {};
      var link = $('[data-cd-pz-own-link]', own);
      var base = own.getAttribute('data-cd-pz-own-base') || '';
      if (link && base) {
        var target = map[String(id)];
        link.href = target ? base + (base.indexOf('?') > -1 ? '&' : '?') + 'variant=' + encodeURIComponent(target) : base;
      }
    }
    var wa = $('[data-cd-pz-wa]', root);
    if (wa) {
      var txt = wa.getAttribute('data-cd-pz-wa-text') || '';
      var waBase = wa.getAttribute('data-cd-pz-wa-base') || '';
      if (txt && waBase) wa.href = waBase + '?text=' + encodeURIComponent(txt.replace(/\)$/, '?variant=' + id + ')'));
    }
  }
  function initLinks() {
    $$('[data-cd-pdp]').forEach(function (root) {
      if (root.__cdPzLinks) return;
      if (!$('[data-cd-pz-own],[data-cd-pz-wa]', root)) return;
      root.__cdPzLinks = true;
      var idInput = $('[data-cd-variant-id]', root);
      if (idInput && idInput.value) syncLinks(root, String(idInput.value));
      document.addEventListener('cadoo:variant', function (e) {
        var id = e && e.detail && e.detail.id;
        if (id) syncLinks(root, String(id));
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  function Controller(root, pz) {
    this.root = root;
    this.pz = pz;
    this.form = $('[data-cd-product-form]', root);
    this.available = (pz.getAttribute('data-cd-pz-available') || '').split(',').filter(Boolean);
    this.method = pz.getAttribute('data-cd-pz-default') || this.available[0] || '';
    this.plainValue = (pz.getAttribute('data-cd-pz-plain') || '').trim();
    this.uploadMb = parseInt(pz.getAttribute('data-cd-pz-upload-mb'), 10) || 10;
    this.uploadBytes = parseInt(pz.getAttribute('data-cd-pz-upload-bytes'), 10) || this.uploadMb * MB;
    this.hasPhoto = this.available.indexOf('photo_upload') > -1;

    this.marker = $('[data-cd-pz-marker]', pz);
    this.methodValue = $('[data-cd-pz-method-value]', pz);
    this.methodGroup = $('[data-cd-pz-group="method"]', pz);
    this.methodInputs = $$('[data-cd-pz-method]', pz);
    this.panels = {};
    var self = this;
    $$('[data-cd-pz-panel]', pz).forEach(function (p) { self.panels[p.getAttribute('data-cd-pz-panel')] = p; });

    this.up = $('[data-cd-up]', pz);
    this.file = $('[data-cd-pz-file]', pz);
    this.existingPhoto = $('[data-cd-pz-existing-photo]', pz);
    this.thumb = $('[data-cd-pz-thumb]', pz);
    this.filename = $('[data-cd-pz-filename]', pz);
    this.stateEl = $('[data-cd-pz-state]', pz);
    this.empty = $('[data-cd-pz-empty]', pz);
    this.selected = $('[data-cd-pz-selected]', pz);
    this.bar = $('[data-cd-up-bar]', pz);
    this.fill = $('[data-cd-up-fill]', pz);
    this.chooseBtn = $('[data-cd-up-choose]', pz);
    this.replaceBtn = $('[data-cd-up-replace]', pz);
    this.removeBtn = $('[data-cd-pz-remove]', pz);
    this.anotherBtn = $('[data-cd-up-another]', pz);
    this.notesGroup = $('[data-cd-pz-group="instructions"]', pz);
    this.instructions = $('[data-cd-pz-instructions]', pz);
    this.insToggle = $('[data-cd-ins-toggle]', pz);
    this.insPanel = $('[data-cd-ins-panel]', pz);
    this.insLabel = $('[data-cd-ins-label]', pz);
    this.qtyNote = $('[data-cd-pz-qty-note]', pz);
    this.editBar = $('[data-cd-pz-editbar]', root);

    this.objectUrl = null;
    this.fileCheck = null;   /* { file, ok:boolean|null } - null while decoding */
    this.phase = 'idle';     /* idle | uploading | error | done */
    this.done = null;        /* { url, name } of the last confirmed upload */
    this.progress = 0;
    this.plainActive = false;
    this.edit = null;        /* { key, line } when editing a bag line */
    this.variants = (readJson($('[data-cd-pdp-data]', root)) || {}).variants || [];

    this.wire();
    this.applyState();
    this.initEdit();
    this.render();
  }

  /* -------- method + visibility -------- */
  Controller.prototype.isPhoto = function () { return this.method === 'photo_upload' && !this.plainActive; };

  /* The single place that decides what is visible and enabled. A field is
     enabled only when its method is active AND no plain variant is chosen. */
  Controller.prototype.applyState = function () {
    var self = this;
    var off = this.plainActive;
    Object.keys(this.panels).forEach(function (m) {
      var on = !off && m === self.method;
      self.panels[m].hidden = !on;
      self.panels[m].disabled = !on;
    });
    if (this.methodGroup) {
      this.methodGroup.hidden = off;
      this.methodInputs.forEach(function (i) {
        i.checked = i.value === self.method;
        i.disabled = off || self.phase === 'uploading';
      });
    }
    if (this.notesGroup) {
      this.notesGroup.hidden = off;
      if (this.instructions) this.instructions.disabled = off;
    }
    if (this.marker) this.marker.disabled = off;
    if (this.methodValue) {
      this.methodValue.value = LABELS[this.method] || '';
      this.methodValue.disabled = off || !this.method;
    }
    this.pz.setAttribute('data-cd-pz-active', off ? 'plain' : this.method);
  };

  Controller.prototype.setMethod = function (m) {
    if (this.available.indexOf(m) < 0 || m === this.method) { this.applyState(); return; }
    if (this.phase === 'uploading') { this.applyState(); return; }   /* locked during an upload */
    this.method = m;
    this.err('general', '');
    this.applyState();
    this.render();
  };

  /* -------- errors -------- */
  Controller.prototype.err = function (which, msg) {
    var el = $('[data-cd-pz-err="' + which + '"]', this.pz);
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
    if (which === 'photo' && this.up) this.up.classList.toggle('has-error', !!msg);
    if (which === 'photo') {
      var field = this.chooseBtn || this.file;
      if (field) { if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid'); }
    }
  };
  /* Error under one text input or one choice group (font / colour). */
  Controller.prototype.fieldErr = function (holder, field, msg) {
    var el = holder && $('[data-cd-pz-err]', holder);
    if (el && el.getAttribute('data-cd-pz-err') === '') { el.textContent = msg || ''; el.hidden = !msg; }
    if (field) { if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid'); }
  };

  Controller.prototype.openPicker = function () {
    if (this.phase === 'uploading' || !this.file) return;
    try { this.file.click(); } catch (e) {}
  };

  Controller.prototype.wire = function () {
    var self = this;
    this.methodInputs.forEach(function (i) {
      i.addEventListener('change', function () { if (i.checked) self.setMethod(i.value); });
    });
    $$('[data-cd-pz-text]', this.pz).forEach(function (t) {
      t.addEventListener('input', function () { if (!blank(t.value)) self.fieldErr(t.closest('.cd-pz__group'), t, ''); });
    });
    if (this.instructions) {
      this.instructions.addEventListener('input', function () { self.syncIns(); });
      this.syncIns();
    }
    if (this.insToggle && this.insPanel) {
      this.insToggle.addEventListener('click', function () {
        var open = self.insPanel.hidden;
        self.setIns(open);
        if (open && self.instructions) { try { self.instructions.focus(); } catch (e) {} }
      });
    }
    /* Selected font / colour names next to each legend; clear a group's error once chosen. */
    var syncSel = function () {
      $$('[data-cd-pz-choice]', self.pz).forEach(function (g) {
        var c = $('input[type="radio"]:checked', g);
        var sel = $('[data-cd-pz-sel]', g);
        if (sel) sel.textContent = c ? (c.getAttribute('data-label') || c.value) : '';
        if (c) self.fieldErr(g, null, '');
      });
    };
    this.pz.addEventListener('change', syncSel); syncSel();
    this.syncSel = syncSel;

    if (this.file) {
      this.file.addEventListener('change', function () { self.onFile(); });
      [this.chooseBtn, this.replaceBtn, this.anotherBtn].forEach(function (b) {
        if (b) b.addEventListener('click', function () { self.openPicker(); });
      });
      if (this.removeBtn) this.removeBtn.addEventListener('click', function () {
        if (self.phase === 'uploading') return;
        self.clearFile(true);
        if (self.chooseBtn) try { self.chooseBtn.focus(); } catch (e) {}
      });
      var drop = $('[data-cd-pz-drop]', this.pz);
      if (drop && window.DataTransfer) {
        ['dragenter', 'dragover'].forEach(function (ev) { drop.addEventListener(ev, function (e) { e.preventDefault(); if (self.phase !== 'uploading') drop.classList.add('is-drag'); }); });
        ['dragleave', 'drop'].forEach(function (ev) { drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-drag'); }); });
        drop.addEventListener('drop', function (e) {
          if (self.phase === 'uploading') return;
          var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (!f) return;
          try { var dt = new DataTransfer(); dt.items.add(f); self.file.files = dt.files; self.onFile(); } catch (err) {}
        });
      }
    }

    var qty = $('[data-cd-qty-input]', this.root);
    var syncQty = function () { if (self.qtyNote) self.qtyNote.hidden = self.plainActive || !(parseInt(qty && qty.value, 10) > 1); };
    if (qty) { qty.addEventListener('input', syncQty); qty.addEventListener('change', syncQty); this.root.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('[data-cd-qty-step]')) setTimeout(syncQty, 0); }); }
    syncQty();

    document.addEventListener('cadoo:variant', function (e) {
      var id = e && e.detail && e.detail.id;
      if (!id) return;
      self.onVariant(String(id));
    });
    var idInput = $('[data-cd-variant-id]', this.root);
    if (idInput && idInput.value) this.onVariant(String(idInput.value));
  };

  /* Notes sit behind a toggle. Collapsing only hides the field - its text is kept and still sent. */
  Controller.prototype.setIns = function (open) {
    if (!this.insPanel || !this.insToggle) return;
    this.insPanel.hidden = !open;
    this.insToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.syncIns();
  };
  Controller.prototype.syncIns = function () {
    if (!this.insLabel || !this.insPanel) return;
    var has = !!(this.instructions && this.instructions.value.trim());
    this.insLabel.textContent = !this.insPanel.hidden ? T.insHide : (has ? T.insEdit : T.insAdd);
  };

  Controller.prototype.variantById = function (id) {
    for (var i = 0; i < this.variants.length; i++) if (String(this.variants[i].id) === String(id)) return this.variants[i];
    return null;
  };

  /* A variant change never changes the chosen method; it only switches the
     whole form off for a configured plain (no personalization) variant. */
  Controller.prototype.onVariant = function (id) {
    var v = this.variantById(id);
    var plain = false;
    if (this.plainValue && v && v.options) {
      plain = v.options.some(function (o) { return String(o).trim() === this.plainValue; }, this);
    }
    if (plain !== this.plainActive) {
      this.plainActive = plain;
      this.applyState();
      if (plain) this.clearErrors();
    }
    syncLinks(this.root, id);
  };

  Controller.prototype.clearErrors = function () {
    var self = this;
    $$('[data-cd-pz-err]', this.pz).forEach(function (e) { e.textContent = ''; e.hidden = true; });
    $$('[aria-invalid]', this.pz).forEach(function (e) { e.removeAttribute('aria-invalid'); });
    if (self.up) self.up.classList.remove('has-error');
  };

  /* ---------------- image selection ---------------- */
  Controller.prototype.currentFile = function () {
    return (this.file && this.file.files && this.file.files[0]) || null;
  };

  Controller.prototype.validateFile = function (f) {
    if (!f) return T.missingPhoto;
    var typeOk = ALLOWED_TYPES.indexOf((f.type || '').toLowerCase()) > -1 || (!f.type && ALLOWED_EXT.test(f.name || ''));
    if (!typeOk) return T.badType;
    if (f.size > this.uploadBytes) return fmt(T.tooBig, { mb: this.uploadMb, size: mb(f.size) });
    if (f.size === 0) return T.unreadable;
    return '';
  };

  Controller.prototype.onFile = function () {
    var self = this;
    var f = this.currentFile();
    if (!f) { this.render(); return; }
    var problem = this.validateFile(f);
    if (problem) {
      this.err('photo', problem);
      this.file.value = '';
      this.fileCheck = null;
      this.render();
      return;
    }
    this.err('photo', '');
    var ge = $('[data-cd-err]', this.root);
    if (ge) { ge.hidden = true; ge.textContent = ''; }
    this.done = null;
    this.phase = 'idle';
    this.releaseUrl();
    try { this.objectUrl = URL.createObjectURL(f); } catch (e) { this.objectUrl = null; }
    var check = { file: f, ok: null };
    this.fileCheck = check;
    this.render();
    /* Make sure the browser can actually decode it (catches renamed / corrupt files). */
    var img = new Image();
    img.onload = function () {
      if (self.fileCheck !== check) return;
      check.ok = !!(img.naturalWidth && img.naturalHeight);
      check.w = img.naturalWidth; check.h = img.naturalHeight;
      if (!check.ok) self.rejectFile(T.unreadable); else self.render();
    };
    img.onerror = function () { if (self.fileCheck === check) self.rejectFile(T.unreadable); };
    if (this.objectUrl) img.src = this.objectUrl; else { check.ok = true; this.render(); }
  };

  Controller.prototype.rejectFile = function (msg) {
    this.file.value = '';
    this.fileCheck = null;
    this.releaseUrl();
    this.err('photo', msg);
    this.render();
  };

  Controller.prototype.releaseUrl = function () {
    if (this.objectUrl) { try { URL.revokeObjectURL(this.objectUrl); } catch (e) {} this.objectUrl = null; }
  };

  Controller.prototype.setProgress = function (p) {
    this.progress = Math.max(0, Math.min(1, p || 0));
    if (this.fill) this.fill.style.width = Math.round(this.progress * 100) + '%';
    if (this.stateEl && this.phase === 'uploading') {
      this.stateEl.textContent = this.progress > 0 ? fmt(T.stUploading, { pct: Math.min(99, Math.round(this.progress * 100)) }) : T.stUploadingNoPct;
    }
  };

  /* Paint the upload panel from the real state. */
  Controller.prototype.render = function () {
    if (!this.up || !this.selected || !this.empty) return;
    var f = this.currentFile();
    var saved = this.edit && this.existingPhoto && !this.existingPhoto.disabled && this.existingPhoto.value;
    var state = 'empty';
    if (f && this.phase === 'uploading') state = 'uploading';
    else if (f && this.phase === 'error') state = 'error';
    else if (f) state = 'ready';
    else if (this.done) state = 'done';
    else if (saved) state = 'saved';
    this.up.setAttribute('data-state', state);
    this.up.setAttribute('aria-busy', state === 'uploading' ? 'true' : 'false');

    var showFile = state !== 'empty';
    this.selected.hidden = !showFile;
    this.empty.hidden = showFile;
    if (this.bar) this.bar.hidden = state !== 'uploading';
    if (this.replaceBtn) { this.replaceBtn.hidden = state === 'done'; this.replaceBtn.disabled = state === 'uploading'; }
    if (this.removeBtn) { this.removeBtn.hidden = state === 'done'; this.removeBtn.disabled = state === 'uploading'; }
    if (this.anotherBtn) this.anotherBtn.hidden = state !== 'done';
    if (!showFile) { if (this.thumb) this.thumb.removeAttribute('src'); return; }

    if (this.filename) this.filename.innerHTML = '';
    if (state === 'done' || state === 'saved') {
      var url = state === 'done' ? this.done.url : this.existingPhoto.value;
      if (this.thumb) { this.thumb.src = sized(url, 240); this.thumb.alt = 'Your uploaded image'; }
      if (this.filename) {
        var a = document.createElement('a');
        /* Opens the in-page photo preview (assets/cadoo-pz-guard.js), not the raw file. */
        a.href = url; a.setAttribute('data-cd-photo-view', '');
        a.textContent = state === 'done' && this.done.name ? this.done.name : 'Your saved photo';
        this.filename.appendChild(a);
      }
      if (this.stateEl) this.stateEl.textContent = state === 'done' ? T.stDone : T.stKept;
      return;
    }
    if (this.thumb) { if (this.objectUrl) this.thumb.src = this.objectUrl; else this.thumb.removeAttribute('src'); this.thumb.alt = 'Preview of your selected image'; }
    if (this.filename) {
      var n = document.createElement('span'); n.className = 'cd-up__fname'; n.textContent = f.name;
      var s = document.createElement('span'); s.className = 'cd-up__fsize'; s.textContent = sizeLabel(f.size) + (this.fileCheck && this.fileCheck.w ? ' · ' + this.fileCheck.w + '×' + this.fileCheck.h + ' px' : '');
      this.filename.appendChild(n); this.filename.appendChild(s);
    }
    if (!this.stateEl) return;
    if (state === 'uploading') this.setProgress(this.progress);
    else if (state === 'error') this.stateEl.textContent = this.edit ? T.stErrorEdit : T.stError;
    else if (this.fileCheck && this.fileCheck.file === f && this.fileCheck.ok === null) this.stateEl.textContent = T.stChecking;
    else this.stateEl.textContent = this.edit ? T.stReadyEdit : T.stReady;
  };

  Controller.prototype.showSelection = function () { this.render(); };

  /* After an upload failure the panel already says what to do; hide cadoo.js's
     copy under the button and bring the panel into view. */
  Controller.prototype.quietButtonError = function () {
    var self = this;
    setTimeout(function () {
      var ge = $('[data-cd-err]', self.root);
      if (ge) { ge.hidden = true; ge.textContent = ''; }
      if (self.up) {
        var r = self.up.getBoundingClientRect();
        if (r.bottom < 60 || r.top > (window.innerHeight || 800) - 60) {
          try { self.up.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
        }
      }
    }, 0);
  };

  /* Clears only the local draft. Files already attached to orders are never touched. */
  Controller.prototype.clearFile = function (alsoSaved) {
    if (this.file) this.file.value = '';
    this.releaseUrl();
    this.fileCheck = null;
    if (this.phase !== 'uploading') this.phase = 'idle';
    this.done = null;
    if (alsoSaved && this.existingPhoto) { this.existingPhoto.value = ''; this.existingPhoto.disabled = true; }
    this.err('photo', '');
    this.render();
  };

  /* ---------------- validation before any request ---------------- */
  Controller.prototype.validate = function () {
    var self = this;
    var first = null;
    var focusPanel = null;
    if (this.plainActive) return { ok: true };
    if (!this.available.length || !this.method) {
      this.err('general', T.blocked);
      first = $('[data-cd-pz-blocked]', this.pz) || this.pz;
    } else {
      this.err('general', '');
      var panel = this.panels[this.method];
      /* Required text: blank or whitespace-only is rejected; nothing else is. */
      $$('[data-cd-pz-text]', panel).forEach(function (t) {
        if (blank(t.value)) { self.fieldErr(t.closest('.cd-pz__group'), t, t.getAttribute('data-cd-pz-req')); first = first || t; }
        else self.fieldErr(t.closest('.cd-pz__group'), t, '');
      });
      /* Required choices (font, colours). */
      $$('[data-cd-pz-choice]', panel).forEach(function (g) {
        if (!$('input[type="radio"]:checked', g)) {
          self.fieldErr(g, null, g.getAttribute('data-cd-pz-choice-msg'));
          first = first || $('input[type="radio"]', g) || g;
        } else self.fieldErr(g, null, '');
      });
      if (this.method === 'photo_upload') {
        var f = this.currentFile();
        var keeping = this.edit && this.existingPhoto && !this.existingPhoto.disabled && hostedUrl(this.existingPhoto.value);
        var focusEl = this.chooseBtn && !this.chooseBtn.closest('[hidden]') ? this.chooseBtn : (this.up || this.file);
        if (this.phase === 'uploading') { this.err('photo', T.busy); first = first || focusEl; focusPanel = this.up; }
        else if (!f && !keeping) {
          this.err('photo', this.done ? T.another : T.missingPhoto);
          first = first || (this.anotherBtn && !this.anotherBtn.hidden ? this.anotherBtn : focusEl);
          focusPanel = this.up;
        } else if (f) {
          var p = this.validateFile(f);
          if (p) { this.err('photo', p); first = first || focusEl; focusPanel = this.up; }
          else if (!this.fileCheck || this.fileCheck.file !== f || this.fileCheck.ok === null) { this.err('photo', T.checking); first = first || focusEl; focusPanel = this.up; }
          else if (this.fileCheck.ok === false) { this.err('photo', T.unreadable); first = first || focusEl; focusPanel = this.up; }
          else if (this.phase !== 'error') this.err('photo', '');
        }
      }
    }
    if (first) {
      try {
        var target = (focusPanel && (first === this.chooseBtn || first === this.up || first === this.file || first === this.anotherBtn)) ? focusPanel : (first.closest ? (first.closest('.cd-pz__group') || first) : first);
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (first.focus) first.focus({ preventScroll: true });
      } catch (e) {}
    }
    return { ok: !first };
  };

  /* Only the active method's keys (plus the shared ones) may leave the page. */
  Controller.prototype.pruneInactive = function (fd) {
    var self = this;
    var keep = {};
    if (!this.plainActive && this.method) {
      SHARED.concat(KEYS[this.method] || []).forEach(function (k) { keep[k] = true; });
    }
    PZ_ALL.forEach(function (k) { if (!keep[k]) fd.delete('properties[' + k + ']'); });
    if (this.plainActive || !this.method) return fd;

    /* Text: ends trimmed only - spelling, case, punctuation and script kept. */
    (KEYS[this.method] || []).concat(['Personalization notes']).forEach(function (k) {
      if (k === 'Your photo') return;
      var name = 'properties[' + k + ']';
      var v = fd.get(name);
      if (typeof v !== 'string') return;
      if (blank(v)) fd.delete(name); else fd.set(name, v.trim());
    });
    fd.set('properties[Personalization]', LABELS[this.method]);
    fd.set('properties[_cadoo_personalized]', '1');

    fd.delete('properties[Your photo]');
    if (this.method === 'photo_upload') {
      var f = this.currentFile();
      if (f) fd.append('properties[Your photo]', f, f.name);
      else if (this.edit && this.existingPhoto && !this.existingPhoto.disabled && hostedUrl(this.existingPhoto.value)) {
        fd.append('properties[Your photo]', this.existingPhoto.value);
      }
    }
    return fd;
  };

  Controller.prototype.buildFormData = function (variantId, qty) {
    var fd = new FormData(this.form);
    fd.set('id', String(variantId));
    fd.set('quantity', String(qty));
    Array.from(fd.keys()).forEach(function (k) {
      if (k !== 'id' && k !== 'quantity' && k.indexOf('properties[') !== 0) fd.delete(k);
    });
    this.pruneInactive(fd);
    /* Edit: carry over everything on the old line EXCEPT personalization
       (wrapping group, add-on and gift keys...). /cart/change.js replaces the
       properties wholesale, so obsolete personalization keys disappear. */
    if (this.edit && this.edit.line && this.edit.line.properties) {
      var own = {};
      Array.from(fd.keys()).forEach(function (k) { own[k] = true; });
      var props = this.edit.line.properties;
      Object.keys(props).forEach(function (k) {
        if (PZ_ALL.indexOf(k) > -1) return;
        var name = 'properties[' + k + ']';
        if (!own[name] && props[k] !== null && props[k] !== undefined && props[k] !== '') fd.append(name, props[k]);
      });
    }
    return fd;
  };

  /* ---------------- the add / save request ---------------- */
  Controller.prototype.isEdit = function () { return !!this.edit; };
  Controller.prototype.busyLabel = function () {
    if (this.edit) return T.saving;
    return this.isPhoto() && this.currentFile() ? T.adding : T.addingPlain;
  };
  Controller.prototype.ctaLabel = function () { return this.edit ? T.save : null; };

  Controller.prototype.lockMethods = function (locked) {
    var self = this;
    this.methodInputs.forEach(function (i) { i.disabled = locked || self.plainActive; });
  };

  Controller.prototype.submit = function (variantId, qty) {
    var self = this;
    var r = routes();
    var sentMethod = this.plainActive ? '' : this.method;
    var fd = this.buildFormData(variantId, qty);
    var op = opId();
    fd.append('properties[_cadoo_op]', op);
    var variantChanged = !!(this.edit && this.edit.line && String(this.edit.line.variant_id) !== String(variantId));
    var url = (this.edit && !variantChanged) ? r.change : r.add;
    if (this.edit && !variantChanged) { fd.set('id', this.edit.key); fd.set('quantity', String(qty)); }
    this.pendingRemove = variantChanged ? this.edit.key : null;

    var photoLine = sentMethod === 'photo_upload';
    var uploading = photoLine && !!this.currentFile();
    var sentName = uploading ? this.currentFile().name : '';
    if (uploading) { this.phase = 'uploading'; this.err('photo', ''); this.progress = 0; this.lockMethods(true); this.render(); }
    var unlock = function () { self.lockMethods(false); };

    /* kind: 'upload' - the photo did not reach the cart (kept for retry);
             'size'   - 413: too large for Shopify; the file is cleared;
             'item'   - the cart refused the item itself; Shopify's reason shown. */
    var fail = function (kind, msg) {
      unlock();
      var kept = uploading && !!self.currentFile();
      if (kind === 'size') {
        if (uploading) { self.clearFile(false); self.err('photo', msg); self.quietButtonError(); }
        else { self.phase = 'idle'; msg = T.tooLong; }
      } else if (kind === 'upload' && kept) {
        self.phase = 'error';
        self.render();
        msg = self.edit ? T.stErrorEdit : T.stError;
        self.quietButtonError();
      } else {
        self.phase = 'idle';
        self.render();
      }
      throw new Error(msg || (self.edit ? T.saveFail : T.addFail));
    };

    var timeoutMs = 120000 + (uploading ? Math.round(this.currentFile().size / 40) : 0);
    return postForm(url, fd, uploading ? function (p) { self.setProgress(p); } : null, timeoutMs)
      .then(function (res) {
        var d = null; try { d = JSON.parse(res.text); } catch (e) {}
        if (res.status === 413) return fail('size', fmt(T.tooBig, { mb: self.uploadMb, size: mb((self.currentFile() || {}).size || 0) }));
        if (res.status >= 400 && res.status < 500 && d && (d.description || d.message)) return fail('item', d.description || d.message);
        if (!res.ok) return fail('upload', self.edit ? T.saveFail : T.addFail);
        return d;
      }, function () {
        /* Uncertain outcome: find out whether the server did it anyway. */
        return getCart().then(function (c) {
          var hit = c && c.items.filter(function (i) { return i.properties && i.properties._cadoo_op === op; })[0];
          if (hit) return hit;
          return fail('upload', self.edit ? T.saveFail : T.addFail);
        });
      })
      .then(function (d) {
        if (uploading) self.setProgress(1);
        return getCart().then(function (c) {
          var line = null;
          if (c) line = c.items.filter(function (i) { return i.properties && i.properties._cadoo_op === op; })[0] || null;
          if (!line && d && d.key) line = d;
          if (!line) return fail('upload', self.edit ? T.saveFail : T.addFail);
          if (photoLine && !hostedUrl(line.properties && line.properties['Your photo'])) {
            /* Accepted without a hosted file - not a success. A NEW line is removed. */
            if (self.edit) return fail('upload', T.saveFail);
            return fetch(r.change, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ id: line.key, quantity: 0 }) })
              .catch(function () {})
              .then(function () { return fail('upload', T.addFail); });
          }
          var finish = function () {
            unlock();
            self.lastLine = line;
            if (photoLine && uploading) {
              var hosted = line.properties['Your photo'];
              if (self.file) self.file.value = '';
              self.releaseUrl();
              self.fileCheck = null;
              self.phase = 'done';
              self.done = { url: hosted, name: sentName };
            } else {
              self.phase = self.done ? 'done' : 'idle';
            }
            self.err('photo', '');
            if (!self.edit) {
              /* Ready for a different personalization of the same item:
                 typed text and notes are cleared, choices kept. */
              $$('[data-cd-pz-text]', self.pz).forEach(function (t) { t.value = ''; });
              if (self.instructions) { self.instructions.value = ''; self.syncIns(); self.setIns(false); }
            }
            self.render();
            return line;
          };
          if (self.pendingRemove) {
            var oldKey = self.pendingRemove; self.pendingRemove = null;
            return fetch(r.change, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ id: oldKey, quantity: 0 }) })
              .then(function (res) { if (!res.ok) throw new Error('old-line'); return res.json(); }, function () { throw new Error('old-line'); })
              .then(function () { return finish(); }, function () { self.leftover = true; return finish(); });
          }
          return finish();
        });
      });
  };

  /* Keep a paid wrapping line in step with the gift line it belongs to after an edit. */
  function syncWrapping(line) {
    var group = line && line.properties && line.properties._cadoo_wrap_group_id;
    if (!group) return Promise.resolve();
    return getCart().then(function (c) {
      if (!c) return;
      var w = c.items.filter(function (i) {
        return i.key !== line.key && i.properties && i.properties._cadoo_wrap_group_id === group && i.properties['Wrapping for'] !== undefined;
      })[0];
      if (!w) return;
      var label = line.product_title + (line.variant_title ? ' — ' + line.variant_title : '');
      var scope = w.properties._cadoo_wrap_scope;
      var qty = scope === 'all' ? line.quantity : Math.min(w.quantity, line.quantity);
      if (w.properties['Wrapping for'] === label && w.quantity === qty) return;
      var props = {};
      Object.keys(w.properties).forEach(function (k) { props[k] = w.properties[k]; });
      props['Wrapping for'] = label;
      return fetch(routes().change, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ id: w.key, quantity: qty, properties: props }) })
        .catch(function () {});
    });
  }

  Controller.prototype.afterEditSaved = function (line) {
    line = line || this.lastLine;
    var back = this.root.getAttribute('data-cd-cart-url') || routes().cartPage;
    var flag = this.leftover ? 'cd_saved=leftover' : 'cd_saved=1';
    this.leftover = false;
    return syncWrapping(line).then(null, function () {}).then(function () {
      window.location.assign(back + (back.indexOf('?') > -1 ? '&' : '?') + flag);
    });
  };

  /* ---------------- edit context ---------------- */
  Controller.prototype.initEdit = function () {
    var key = null;
    try { key = new URL(window.location.href).searchParams.get('cd_edit'); } catch (e) {}
    if (!key) return;
    var self = this;
    getCart().then(function (c) {
      var line = c && c.items.filter(function (i) { return i.key === key; })[0];
      if (!line) {
        var errEl = $('[data-cd-err]', self.root);
        if (errEl) { errEl.textContent = T.noLine; errEl.hidden = false; }
        return;
      }
      self.edit = { key: key, line: line };
      self.prefill(line);
      self.root.setAttribute('data-cd-pz-editing', '');
      var label = $('[data-cd-submit-label]', self.root);
      if (label) label.textContent = T.save;
      var sticky = $('[data-cd-sticky-btn] span', self.root);
      if (sticky) sticky.textContent = T.save;
      if (self.editBar) self.editBar.hidden = false;
      self.render();
      try { document.dispatchEvent(new CustomEvent('cadoo:pz-edit', { detail: { key: key } })); } catch (e) {}
    });
  };

  /* Select a saved value in a radio group; a value no longer offered becomes a
     visible "Previously chosen" option that stays selected until changed. */
  Controller.prototype.pickSaved = function (panel, field, value) {
    if (!panel || blank(value)) return;
    var radios = $$('input[type="radio"][name="properties[' + field + ']"]', panel);
    if (!radios.length) return;
    var hit = radios.filter(function (i) { return i.value === value || i.getAttribute('data-label') === value; })[0];
    if (hit) { hit.checked = true; return; }
    var group = radios[0].closest('[data-cd-pz-choice]');
    var wrap = radios[0].parentNode && radios[0].parentNode.parentNode;
    if (!group || !wrap) return;
    var id = radios[0].id + '-saved';
    var span = document.createElement('span');
    span.className = 'cd-pz__legacy';
    var inp = document.createElement('input');
    inp.type = 'radio'; inp.id = id; inp.name = 'properties[' + field + ']'; inp.value = value;
    inp.setAttribute('data-label', value); inp.checked = true;
    var lab = document.createElement('label');
    lab.setAttribute('for', id);
    lab.textContent = fmt(T.previously, { v: value });
    span.appendChild(inp); span.appendChild(lab);
    wrap.parentNode.insertBefore(span, wrap.nextSibling);
  };

  Controller.prototype.prefill = function (line) {
    var p = line.properties || {};
    var m = lineMethod(p);
    if (m && this.available.indexOf(m) > -1) {
      this.method = m;
    } else if (m) {
      this.err('general', T.methodGone);
    }
    this.applyState();
    var panel = this.panels[this.method];
    var setText = function (pnl, field, value) {
      var t = pnl && $('[name="properties[' + field + ']"]', pnl);
      if (t && !blank(value)) { t.value = value; t.dispatchEvent(new Event('input')); }
    };
    if (m === this.method) {
      if (m === 'name_initials') {
        setText(panel, 'Name or initials', p['Name or initials'] || p.Name || p.Initials || '');
        this.pickSaved(panel, 'Font', p.Font);
        this.pickSaved(panel, 'Print colour', p['Print colour']);
      } else if (m === 'letter_name') {
        setText(panel, 'Letter', p.Letter);
        setText(panel, 'Name', p.Name);
        this.pickSaved(panel, 'Letter colour', p['Letter colour']);
        this.pickSaved(panel, 'Name colour', p['Name colour']);
        this.pickSaved(panel, 'Name font', p['Name font']);
      } else if (m === 'photo_upload' && this.existingPhoto && hostedUrl(p['Your photo'])) {
        this.existingPhoto.value = p['Your photo'];
        this.existingPhoto.disabled = false;
      }
    }
    if (this.syncSel) this.syncSel();
    var notes = p['Personalization notes'] || p.Instructions || '';
    if (this.instructions && !blank(notes)) { this.instructions.value = notes; this.syncIns(); this.setIns(true); }
    var qty = $('[data-cd-qty-input]', this.root);
    if (qty && line.quantity > 0) { qty.value = line.quantity; qty.dispatchEvent(new Event('change')); }
  };

  /* ---------------- registry ---------------- */
  var controllers = [];
  function forRoot(root) {
    if (!root) return null;
    var pz = $('[data-cd-pz]', root);
    if (!pz) return null;
    for (var i = 0; i < controllers.length; i++) if (controllers[i].root === root && controllers[i].pz === pz) return controllers[i];
    var c = new Controller(root, pz);
    controllers.push(c);
    return c;
  }

  window.CadooPersonalize = { forRoot: forRoot, hostedUrl: hostedUrl, safeHref: safeHref, lineMethod: lineMethod, labels: LABELS };

  function init() { initLinks(); $$('[data-cd-pdp]').forEach(function (root) { forRoot(root); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
